import { useState, useEffect, useRef } from 'react';
import { useForm, Controller, type ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert, Button, Input, ItemList, ItemListRow, SearchCombo, Skeleton, StatusHint,
} from '@indipia/ui-core';
import { stepASchema, type StepAFormValues } from './formSchemas';
import { checkCustomerByMobile, createCustomer, searchAddress, fetchSitesByCustomer, updateSiteAccess } from './api';
import { formatPhone, formatPhoneInput } from '../external-link-send/utils/phone';
import type { AddressSearchItem, SiteListItem, StepAResult } from './types';

interface StepAProps {
  onNext: (result: StepAResult) => void;
  onSelectExistingSite?: (siteId: number) => void;
  /** 초기값 — 고객명·연락처를 미리 채운다(견적서 추출 결과 등) */
  prefill?: { customerName?: string; customerMobile?: string };
}

function scrollPopupDown(el: HTMLElement | null) {
  if (!el) return;
  let parent = el.parentElement;
  while (parent && parent !== document.body && parent.tagName !== 'MAIN') {
    const style = getComputedStyle(parent);
    if (parent.scrollHeight > parent.clientHeight && style.overflowY !== 'hidden' && style.overflowY !== 'visible') {
      parent.scrollTo({ top: parent.scrollHeight, behavior: 'smooth' });
      return;
    }
    parent = parent.parentElement;
  }
}

export function StepA({ onNext, onSelectExistingSite, prefill }: StepAProps) {
  const [registeredCustomerId, setRegisteredCustomerId] = useState<number | null>(null);
  const addrSectionRef = useRef<HTMLDivElement>(null);

  // 고객 주소 검색
  const [addrQuery, setAddrQuery] = useState('');
  const [debouncedAddrQuery, setDebouncedAddrQuery] = useState('');
  const [selectedAddr, setSelectedAddr] = useState<AddressSearchItem | null>(null);
  const [confirmedAddr, setConfirmedAddr] = useState('');

  // 연락처 디바운스
  const [debouncedMobile, setDebouncedMobile] = useState('');

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StepAFormValues>({
    resolver: zodResolver(stepASchema),
    mode: 'onBlur',
    defaultValues: {
      customerName: prefill?.customerName ?? '',
      // 연락처는 하이픈 포맷으로 맞춰 둬야 중복체크 패턴(010-0000-0000)에 걸린다
      customerMobile: prefill?.customerMobile ? formatPhoneInput(prefill.customerMobile) : '',
    },
  });

  const mobileValue = watch('customerMobile') ?? '';
  const mobilePattern = /^01[0-9]-\d{3,4}-\d{4}$/;
  const mobileComplete = mobilePattern.test(mobileValue);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedMobile(mobileValue), 300);
    return () => clearTimeout(id);
  }, [mobileValue]);

  useEffect(() => { setRegisteredCustomerId(null); }, [mobileValue]);

  useEffect(() => {
    if (selectedAddr) return;
    const id = setTimeout(() => setDebouncedAddrQuery(addrQuery), 300);
    return () => clearTimeout(id);
  }, [addrQuery, selectedAddr]);

  const selectSiteMutation = useMutation<void, Error, number>({
    mutationFn: updateSiteAccess,
    onSuccess: (_: void, siteId: number) => { onSelectExistingSite?.(siteId); },
  });

  const { data: dupResult, isFetching: isChecking } = useQuery({
    queryKey: ['customer-mobile', debouncedMobile],
    queryFn: () => checkCustomerByMobile(debouncedMobile),
    enabled: mobilePattern.test(debouncedMobile),
    staleTime: 10_000,
  });

  // 기존 고객 감지 시 고객명 자동 입력
  useEffect(() => {
    if (dupResult?.exists && dupResult.customerName) {
      setValue('customerName', dupResult.customerName, { shouldValidate: true });
    }
  }, [dupResult, setValue]);

  const {
    data: existingSites = [],
    isFetching: isSitesFetching,
    isError: isSitesError,
  } = useQuery({
    queryKey: ['customer-sites', dupResult?.customerId],
    queryFn: () => fetchSitesByCustomer(dupResult!.customerId!),
    enabled: dupResult?.exists === true && !!dupResult?.customerId,
    staleTime: 30_000,
  });

  const { data: addrResults = [], isFetching: isAddrSearching, isError: isAddrError, error: addrError } = useQuery({
    queryKey: ['address-search-a', debouncedAddrQuery],
    queryFn: () => searchAddress(debouncedAddrQuery),
    enabled: debouncedAddrQuery.trim().length >= 2 && !selectedAddr,
    staleTime: 30_000,
  });

  // 주소 검색 결과 드롭다운이 열릴 때 팝업 내부 스크롤 내리기
  useEffect(() => {
    if (addrResults.length === 0 && !isAddrSearching) return;
    scrollPopupDown(addrSectionRef.current);
  }, [addrResults.length, isAddrSearching]);

  const registerMutation = useMutation({
    mutationFn: (data: StepAFormValues) =>
      createCustomer({
        customerName: data.customerName,
        customerMobile: data.customerMobile,
        customerAddress: confirmedAddr || undefined,
        // 시군구코드 = 고객 주소 admCd 앞5 (현장 생성 시 전파되어 시공반장 매칭에 사용)
        sigunguCode: selectedAddr?.admCd ? selectedAddr.admCd.slice(0, 5) : undefined,
      }),
    onSuccess: ({ customerId }, variables) => {
      setRegisteredCustomerId(customerId);
      toast.success('고객이 등록되었습니다.');
      // 새 고객 저장 성공 시 [다음] 클릭 없이 바로 Step B로 진행
      onNext({
        customerId,
        customerName: variables.customerName,
        customerMobile: variables.customerMobile,
        customerAddress: confirmedAddr || undefined,
        addressItem: selectedAddr ?? undefined,
      });
    },
    onError: (e) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '고객 등록에 실패했습니다.';
      toast.error(msg);
    },
  });

  async function handleRegister() {
    const values = await new Promise<StepAFormValues | null>((resolve) => {
      void handleSubmit(
        (data) => resolve(data),
        () => resolve(null),
      )();
    });
    if (!values) return;
    registerMutation.mutate(values);
  }

  function handleSelectAddr(item: AddressSearchItem) {
    setSelectedAddr(item);
    setAddrQuery('');
    setDebouncedAddrQuery('');
    setConfirmedAddr(item.roadAddress);
  }

  const onSubmit = async (values: StepAFormValues) => {
    if (dupResult?.exists && dupResult.customerId) {
      onNext({
        customerId: dupResult.customerId,
        customerName: dupResult.customerName ?? values.customerName,
        customerMobile: dupResult.customerMobile ?? values.customerMobile,
        customerAddress: confirmedAddr || undefined,
        addressItem: selectedAddr ?? undefined,
      });
      return;
    }
    if (registeredCustomerId !== null) {
      onNext({
        customerId: registeredCustomerId,
        customerName: values.customerName,
        customerMobile: values.customerMobile,
        customerAddress: confirmedAddr || undefined,
        addressItem: selectedAddr ?? undefined,
      });
      return;
    }
    toast.error('먼저 [새 현장으로 등록하기]를 눌러 고객을 등록해 주세요.');
  };

  const isExistingCustomer = dupResult?.exists === true;
  const isNewCustomerReady = !isExistingCustomer && registeredCustomerId !== null;
  const canProceed = isExistingCustomer || isNewCustomerReady;

  return (
    <div className="space-y-4">

      {/* 고객명 — 라벨·필수(*)·에러 도움말은 Input 이 렌더한다 (D-09 §15.2) */}
      <Input
        id="stepA-customerName"
        label="고객명"
        required
        width="auto"
        type="text"
        placeholder="예) 홍길동"
        // 기존 고객이면 이름이 서버 값으로 잠긴다 — readOnly 는 표준에 시각 규격이 없어
        // disabled(gray-200 fill · §15.2)로 "잠김"을 드러낸다. 값은 RHF 상태에 남는다.
        disabled={isExistingCustomer}
        error={errors.customerName?.message}
        {...register('customerName')}
      />

      {/* 연락처 */}
      <div>
        <Controller
          name="customerMobile"
          control={control}
          render={({ field }: { field: ControllerRenderProps<StepAFormValues, 'customerMobile'> }) => (
            <Input
              {...field}
              id="stepA-mobile"
              label="연락처"
              required
              width="auto"
              type="tel"
              placeholder="010-0000-0000"
              error={errors.customerMobile?.message}
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
            />
          )}
        />

        {mobileComplete && isChecking && (
          <div className="mt-1.5"><Skeleton variant="text" width={120} /></div>
        )}

        {/* 기존 고객 안내 — 아이콘·제목은 Alert 이 갖는다 (D-09 §15.10) */}
        {mobileComplete && !isChecking && isExistingCustomer && dupResult && (
          <div className="mt-2 space-y-2">
            <Alert kind="warning" title="기존 고객입니다">
              {dupResult.customerName} · {formatPhone(dupResult.customerMobile ?? '')}
            </Alert>

            {addrQuery.length > 0 ? (
              // 주소 검색 중 — 드롭다운 공간 확보를 위해 접힘
              <StatusHint tone="muted">주소 검색 완료 후 현장 목록이 표시됩니다.</StatusHint>
            ) : isSitesFetching ? (
              <Skeleton height={64} />
            ) : isSitesError ? (
              <Alert kind="error">현장 목록을 불러올 수 없습니다.</Alert>
            ) : existingSites.length > 0 ? (
              <div>
                <p className="mb-1 text-caption font-semibold text-text-secondary">등록된 현장</p>
                <ItemList>
                  {/* trailing 은 ItemListRow 가 이미 <button> 이라 중첩을 피해 모양만 세컨더리 sm 버튼으로 준다.
                      클릭은 행 전체(onClick)가 받는다. */}
                  {existingSites.map((site: SiteListItem) => (
                    <ItemListRow
                      key={site.siteId}
                      disabled={selectSiteMutation.isPending}
                      onClick={() => selectSiteMutation.mutate(site.siteId)}
                      title={site.siteName}
                      hint={
                        <StatusHint tone="muted">
                          {site.siteAddress}{site.addressDong ? ` ${site.addressDong}동` : ''}{site.addressHo ? ` ${site.addressHo}호` : ''}
                        </StatusHint>
                      }
                      trailing={<span className="btn btn-secondary btn-sm" aria-hidden="true">선택</span>}
                    />
                  ))}
                </ItemList>
                <p className="mt-2 text-caption text-text-muted">
                  또는 아래 고객 주소를 입력 후 [다음 →]을 눌러 이 고객으로 새 현장을 등록하세요.
                </p>
              </div>
            ) : (
              <StatusHint tone="muted">등록된 현장이 없습니다. [다음 →]을 눌러 새 현장을 등록하세요.</StatusHint>
            )}
          </div>
        )}

        {mobileComplete && !isChecking && dupResult != null && !isExistingCustomer && registeredCustomerId === null && (
          <Alert kind="success" className="mt-2">신규고객입니다.</Alert>
        )}

        {mobileComplete && !isChecking && registeredCustomerId !== null && (
          <Alert kind="success" className="mt-2">고객 등록 완료</Alert>
        )}
      </div>

      {/* 고객 주소 — Search 는 label prop 이 없어 .field 구조를 직접 만든다.
          라벨 타이포·.opt 색은 .field > label 이 갖고, .field-auto 가 320 고정폭을 푼다(D-09 §15.2) */}
      <div ref={addrSectionRef} className="field field-auto">
        <label htmlFor="stepA-address">
          고객 주소 <span className="opt">(선택 · Step B 자동 연동)</span>
        </label>

        {confirmedAddr ? (
          <div className="flex items-center gap-2">
            {/* 확정된 주소는 표시 전용 readOnly Input — StepB 현장 주소와 동일 패턴 */}
            <Input id="stepA-address" value={confirmedAddr} readOnly width="fill" />
            <Button
              variant="etc"
              size="md"
              type="button"
              onClick={() => { setConfirmedAddr(''); setSelectedAddr(null); setAddrQuery(''); }}
            >
              변경
            </Button>
          </div>
        ) : (
          <div className="relative">
            {/* onSearch(버튼·Enter 공통)는 300ms 디바운스를 건너뛰고 즉시 검색을 건다.
                타이핑만 해도 기존 디바운스 자동 검색은 그대로 동작한다. */}
            <SearchCombo
              id="stepA-address"
              value={addrQuery}
              onChange={setAddrQuery}
              onSearch={setDebouncedAddrQuery}
              placeholder="도로명 주소를 검색하세요"
            />
            {isAddrError && (
              <Alert kind="error" className="mt-1.5">
                {(addrError as { response?: { data?: { message?: string } } } | null)?.response?.data?.message ?? '주소 검색에 실패했습니다. 다시 시도해주세요.'}
              </Alert>
            )}
            {(addrResults.length > 0 || isAddrSearching) && (
              <div className="absolute top-full z-dropdown mt-1 max-h-52 w-full overflow-y-auto rounded-xxl shadow-[shadow:var(--elevation-2)]">
                {isAddrSearching && (
                  <div className="rounded-xxl border border-border-default bg-surface-raised p-3">
                    <Skeleton variant="text" width="60%" />
                  </div>
                )}
                {addrResults.length > 0 && (
                  <ItemList>
                    {addrResults.map((item: AddressSearchItem, i: number) => (
                      <ItemListRow
                        key={i}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectAddr(item)}
                        title={item.roadAddress}
                        hint={item.buildingName ? <StatusHint tone="muted">{item.buildingName}</StatusHint> : undefined}
                      />
                    ))}
                  </ItemList>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 새 현장으로 등록하기 */}
      {mobileComplete && !isChecking && !isExistingCustomer && dupResult != null && registeredCustomerId === null && (
        <Button
          variant="secondary"
          type="button"
          onClick={() => void handleRegister()}
          disabled={registerMutation.isPending}
          className="w-full"
        >
          {registerMutation.isPending ? '등록 중…' : '새 현장으로 등록하기'}
        </Button>
      )}

      {/* 하단 버튼 */}
      <div className="flex gap-2 pt-1">
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={() => void handleSubmit(onSubmit)()}
          disabled={!canProceed}
          className="flex-1"
        >
          다음 →
        </Button>
      </div>
    </div>
  );
}
